import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle, Filter, RotateCw, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import SEOHead from '../components/SEOHead';
import Footer from '../components/Footer';
import FiltrationCTABanner from '../components/FiltrationCTABanner';

const InlineFiltersPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Inline Pressure Filters — Reliable High-Capacity Filtration | CTC Waterworks"
        description="CTC Inline Pressure Filters protect pressurized irrigation systems. Choose BIG Series for high-flow automation-ready filtration or TIG Series for compact manual systems."
        canonicalPath="/filtration/inline"
      />
      <Navbar />
      <FiltrationCTABanner />

      {/* Breadcrumbs */}
      <nav aria-label="Breadcrumb" className="bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-3">
          <ol className="flex items-center space-x-2 text-sm">
            <li>
              <Link to="/" className="text-gray-600 hover:text-ctc-blue">Home</Link>
            </li>
            <li className="text-gray-400">/</li>
            <li>
              <Link to="/filtration" className="text-gray-600 hover:text-ctc-blue">Filtration</Link>
            </li>
            <li className="text-gray-400">/</li>
            <li className="text-gray-900 font-semibold">Inline Pressure Filters</li>
          </ol>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-16 pb-12 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-5xl font-bold text-gray-900 mb-4">
              Inline Pressure Filters
            </h1>
            <p className="text-2xl text-gray-600 mb-8 font-light">
              Reliable, high-capacity filtration for pressurized irrigation systems.
            </p>

            <div className="bg-blue-50 border-l-4 border-ctc-blue p-6 rounded-r-lg mb-8">
              <p className="text-gray-800 leading-relaxed text-lg">
                Inline pressure filters protect emitters, valves, and downstream equipment by removing suspended solids from pressurized water. CTC offers multiple series — BIG, BIG-S, GNG, and TIG — each designed for specific applications, flow ranges, budget considerations, and operational requirements. All deliver true inline flow, low head loss, and rugged construction for long-term performance.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Product Comparison Cards */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Choose Your Inline Filter Series</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            {/* BIG Series Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-white rounded-lg shadow-xl overflow-hidden border-2 border-ctc-blue hover:shadow-2xl transition-all flex flex-col"
            >
              <div className="bg-gradient-to-r from-ctc-blue to-ctc-blue-dark p-6">
                <Filter className="text-white mb-3" size={40} />
                <h3 className="text-xl font-bold text-white">BIG Series</h3>
              </div>

              <div className="p-6 flex-1 flex flex-col">
                <p className="text-gray-700 mb-6 leading-relaxed">
                  Precision, gasket-sealed in-line filters for leak-free, consistent filtration.
                </p>

                <div className="mt-auto space-y-3">
                  <Link
                    to="/filtration/inline/big"
                    className="block w-full bg-gradient-to-r from-ctc-blue to-ctc-blue-dark text-white px-6 py-3 rounded-lg font-semibold hover:from-ctc-blue-dark hover:to-ctc-blue transition-all shadow-lg text-center"
                  >
                    Learn More
                  </Link>
                  <Link
                    to="/filtration/self-cleaning"
                    className="block w-full text-center text-sm text-ctc-blue hover:text-ctc-blue-dark transition-colors"
                  >
                    Compare cleaning options
                  </Link>
                </div>
              </div>
            </motion.div>

            {/* BIG-S Series Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="bg-white rounded-lg shadow-xl overflow-hidden border-2 border-gray-300 hover:border-ctc-blue hover:shadow-2xl transition-all flex flex-col"
            >
              <div className="bg-gradient-to-r from-gray-600 to-gray-700 p-6">
                <Filter className="text-white mb-3" size={40} />
                <h3 className="text-xl font-bold text-white">BIG-S (Stainless)</h3>
              </div>

              <div className="p-6 flex-1 flex flex-col">
                <p className="text-gray-700 mb-6 leading-relaxed">
                  Precision filtration in a cost-optimized stainless body; modular ports for low-cost installs.
                </p>

                <div className="mt-auto space-y-3">
                  <Link
                    to="/filtration/inline/big-s"
                    className="block w-full bg-gradient-to-r from-gray-600 to-gray-700 text-white px-6 py-3 rounded-lg font-semibold hover:from-gray-700 hover:to-gray-800 transition-all shadow-lg text-center"
                  >
                    Learn More
                  </Link>
                  <Link
                    to="/filtration/self-cleaning"
                    className="block w-full text-center text-sm text-ctc-blue hover:text-ctc-blue-dark transition-colors"
                  >
                    Compare cleaning options
                  </Link>
                </div>
              </div>
            </motion.div>

            {/* GNG Series Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-white rounded-lg shadow-xl overflow-hidden border-2 border-gray-300 hover:border-ctc-blue hover:shadow-2xl transition-all flex flex-col"
            >
              <div className="bg-gradient-to-r from-green-600 to-green-700 p-6">
                <Filter className="text-white mb-3" size={40} />
                <h3 className="text-xl font-bold text-white">GNG (Economy)</h3>
              </div>

              <div className="p-6 flex-1 flex flex-col">
                <p className="text-gray-700 mb-6 leading-relaxed">
                  Budget-friendly in-line filtration with ring-lock quick-release head.
                </p>

                <div className="mt-auto space-y-3">
                  <Link
                    to="/filtration/inline/gng"
                    className="block w-full bg-gradient-to-r from-green-600 to-green-700 text-white px-6 py-3 rounded-lg font-semibold hover:from-green-700 hover:to-green-800 transition-all shadow-lg text-center"
                  >
                    Learn More
                  </Link>
                  <Link
                    to="/filtration/self-cleaning"
                    className="block w-full text-center text-sm text-ctc-blue hover:text-ctc-blue-dark transition-colors"
                  >
                    Compare cleaning options
                  </Link>
                </div>
              </div>
            </motion.div>

            {/* TIG Series Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="bg-white rounded-lg shadow-xl overflow-hidden border-2 border-gray-300 hover:border-ctc-blue hover:shadow-2xl transition-all flex flex-col"
            >
              <div className="bg-gradient-to-r from-orange-600 to-orange-700 p-6">
                <Filter className="text-white mb-3" size={40} />
                <h3 className="text-xl font-bold text-white">TIG Series</h3>
              </div>

              <div className="p-6 flex-1 flex flex-col">
                <p className="text-gray-700 mb-6 leading-relaxed">
                  Compact inline design with quick-access end caps. Ideal for space-constrained installations.
                </p>

                <div className="mt-auto space-y-3">
                  <Link
                    to="/filtration/inline/tig"
                    className="block w-full bg-gradient-to-r from-orange-600 to-orange-700 text-white px-6 py-3 rounded-lg font-semibold hover:from-orange-700 hover:to-orange-800 transition-all shadow-lg text-center"
                  >
                    Learn More
                  </Link>
                  <div className="text-center text-sm text-gray-600">
                    True inline flow path
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Automation Upsell for BIG */}
      <section className="py-16 bg-gradient-to-br from-blue-50 to-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="bg-white rounded-lg shadow-xl p-8 border-2 border-ctc-blue">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">Upgrade to Automated Cleaning</h2>
            <p className="text-gray-700 text-lg mb-8 text-center max-w-3xl mx-auto leading-relaxed">
              BIG Series filters can be equipped with Brush or Vacuum Self-Cleaning technology for automatic operation and minimal maintenance. Transform your manual filter into a continuous-duty system with no operator input required.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              {/* Brush Cleaning Option */}
              <div className="bg-gradient-to-br from-green-50 to-white border-2 border-green-200 rounded-lg p-6">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center mr-4">
                    <RotateCw className="text-white" size={24} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">Brush Cleaning</h3>
                </div>
                <p className="text-gray-700 mb-4 leading-relaxed">
                  Rotating internal brushes mechanically scrub the screen surface to remove organic debris and moderate solids loads. Ideal for surface water sources and algae-prone conditions.
                </p>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-start text-sm">
                    <CheckCircle className="text-green-600 mr-2 mt-0.5 flex-shrink-0" size={16} />
                    <span className="text-gray-700">Low water consumption</span>
                  </li>
                  <li className="flex items-start text-sm">
                    <CheckCircle className="text-green-600 mr-2 mt-0.5 flex-shrink-0" size={16} />
                    <span className="text-gray-700">Simple motorized drive</span>
                  </li>
                  <li className="flex items-start text-sm">
                    <CheckCircle className="text-green-600 mr-2 mt-0.5 flex-shrink-0" size={16} />
                    <span className="text-gray-700">Retrofits existing BIG filters</span>
                  </li>
                </ul>
                <Link
                  to="/filtration/inline/big/brush-cleaning"
                  className="inline-flex items-center text-green-600 font-semibold hover:text-green-700 transition-colors"
                >
                  <span>Explore Brush Cleaning</span>
                  <ArrowRight size={18} className="ml-2" />
                </Link>
              </div>

              {/* Vacuum Cleaning Option */}
              <div className="bg-gradient-to-br from-orange-50 to-white border-2 border-orange-200 rounded-lg p-6">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-orange-600 rounded-full flex items-center justify-center mr-4">
                    <Zap className="text-white" size={24} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">Vacuum Cleaning</h3>
                </div>
                <p className="text-gray-700 mb-4 leading-relaxed">
                  High-velocity vacuum scanner sweeps the screen during short backflush cycles, enabling continuous operation under pressure. Handles high and variable solids loads.
                </p>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-start text-sm">
                    <CheckCircle className="text-orange-600 mr-2 mt-0.5 flex-shrink-0" size={16} />
                    <span className="text-gray-700">Continuous filtration during cleaning</span>
                  </li>
                  <li className="flex items-start text-sm">
                    <CheckCircle className="text-orange-600 mr-2 mt-0.5 flex-shrink-0" size={16} />
                    <span className="text-gray-700">Rapid 10-20 second cycles</span>
                  </li>
                  <li className="flex items-start text-sm">
                    <CheckCircle className="text-orange-600 mr-2 mt-0.5 flex-shrink-0" size={16} />
                    <span className="text-gray-700">Programmable control panel</span>
                  </li>
                </ul>
                <Link
                  to="/filtration/inline/big/vacuum-cleaning"
                  className="inline-flex items-center text-orange-600 font-semibold hover:text-orange-700 transition-colors"
                >
                  <span>Explore Vacuum Cleaning</span>
                  <ArrowRight size={18} className="ml-2" />
                </Link>
              </div>
            </div>

            <div className="text-center">
              <Link
                to="/filtration/self-cleaning"
                className="inline-flex items-center justify-center bg-gradient-to-r from-ctc-blue to-ctc-blue-dark text-white px-8 py-4 rounded-lg font-semibold hover:from-ctc-blue-dark hover:to-ctc-blue transition-all shadow-lg"
              >
                <span>Compare All Automatic Self-Cleaning Options</span>
                <ArrowRight size={20} className="ml-2" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Key Benefits */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Why Choose CTC Inline Filters?</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="flex items-start">
              <div className="flex-shrink-0 w-12 h-12 bg-ctc-blue/10 rounded-lg flex items-center justify-center mr-4">
                <Filter className="text-ctc-blue" size={24} />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">True Inline Flow Path</h3>
                <p className="text-gray-700 leading-relaxed">
                  Minimize head loss and maintain system pressure with straight-through flow design. No bends, no restrictions.
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="flex-shrink-0 w-12 h-12 bg-green-600/10 rounded-lg flex items-center justify-center mr-4">
                <CheckCircle className="text-green-600" size={24} />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Rugged Construction</h3>
                <p className="text-gray-700 leading-relaxed">
                  Heavy-duty carbon steel or stainless steel housings rated for 150 PSI. Built for decades of service in demanding conditions.
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="flex-shrink-0 w-12 h-12 bg-orange-600/10 rounded-lg flex items-center justify-center mr-4">
                <RotateCw className="text-orange-600" size={24} />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Flexible Screen Options</h3>
                <p className="text-gray-700 leading-relaxed">
                  Standard mesh sizes (20–100), perforated plate, wedgewire, and custom configurations available. Choose the exact filtration level for your application — we can match any specification.
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="flex-shrink-0 w-12 h-12 bg-ctc-blue/10 rounded-lg flex items-center justify-center mr-4">
                <Zap className="text-ctc-blue" size={24} />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Future-Proof Design</h3>
                <p className="text-gray-700 leading-relaxed">
                  BIG Series filters accept automation upgrades at any time. Start manual, upgrade to automatic as your operation grows.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-gradient-to-r from-ctc-blue to-ctc-blue-dark text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold mb-6">Protect Every Drop — in Any Application</h2>
          <p className="text-xl text-white/90 mb-8">
            Whether you're safeguarding irrigation lines, pre-treating municipal water, filtering industrial process streams, or protecting hydropower intakes, CTC Inline Pressure Filters deliver reliable, stainless-steel performance you can trust.
          </p>
          <p className="text-lg text-white/80 mb-8">
            Talk to a Filtration Specialist today to get a system tailored to your flow rates, solids profile, and operational needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact?topic=filtration"
              className="inline-flex items-center justify-center bg-white text-ctc-blue px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-lg"
            >
              <span>Talk to a Filtration Specialist</span>
              <ArrowRight size={20} className="ml-2" />
            </Link>
            <a
              href="tel:+15099017476"
              className="inline-flex items-center justify-center bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-ctc-blue transition-colors"
            >
              <span>(509) 901-7476</span>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default InlineFiltersPage;
